import type { RealtimeClient } from "@tcg/realtime";
import { RealtimeProvider } from "@tcg/realtime/react";
import {
  type ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "@/auth/use-auth";
import { ApiConfigContext } from "@/lib/api/context";

import { createAdminRealtime } from "./client";
import { LiveAlerts } from "./LiveAlerts";

/** The tab's client, opened / closed by the provider's effect (an external system for React). */
function createClientHolder() {
  let current: { key: string; client: RealtimeClient } | null = null;
  const listeners = new Set<() => void>();
  const emit = () => {
    for (const listener of listeners) {
      listener();
    }
  };
  return {
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    get: (): RealtimeClient | null => current?.client ?? null,
    open(key: string, make: () => RealtimeClient) {
      if (current?.key === key) {
        return;
      }
      current?.client.close();
      current = { key, client: make() };
      emit();
    },
    /** Close the client of `key` (`null` = whichever is open). */
    release(key: string | null) {
      if (current && (key === null || current.key === key)) {
        current.client.close();
        current = null;
        emit();
      }
    },
  };
}

export interface AdminRealtimeProviderProps {
  children: ReactNode;
  /** `false` = realtime switched off (every live screen polls every 30 s). */
  enabled?: boolean;
  /** Tests: the client to use instead of the one built from the API config. */
  client?: RealtimeClient | null;
}

/**
 * One realtime client per signed-in staff member and tab (T11-52, ADR-0010): built from the same
 * gateway root and `fetch` as every API call, with the staff bearer (refreshed when the hub says
 * it is no longer valid, renewed on the open stream after a silent refresh). Signing out closes
 * it; another person signing in on this tab gets a new one. Also mounts the live alerts.
 */
export function AdminRealtimeProvider({
  children,
  enabled = true,
  client: injected,
}: AdminRealtimeProviderProps) {
  const api = useContext(ApiConfigContext);
  const { session, getAccessToken, refreshNow } = useAuth();
  const { i18n } = useTranslation();
  const staffId = session?.user.id ?? null;
  const live = useRef({ getAccessToken, refreshNow, i18n });
  useEffect(() => {
    live.current = { getAccessToken, refreshNow, i18n };
  });
  const [holder] = useState(createClientHolder);
  const client = useSyncExternalStore(holder.subscribe, holder.get, holder.get);
  const key =
    injected !== undefined || !enabled || staffId === null ? null : `${staffId}|${api.baseUrl}`;

  useEffect(() => {
    if (key === null) {
      holder.release(null);
      return;
    }
    holder.open(key, () =>
      createAdminRealtime({
        baseUrl: api.baseUrl,
        ...(api.fetch ? { fetch: api.fetch } : {}),
        getToken: (force) => (force ? live.current.refreshNow() : live.current.getAccessToken()),
        locale: () => live.current.i18n.resolvedLanguage ?? null,
      }),
    );
    return () => {
      holder.release(key);
    };
  }, [holder, key, api.baseUrl, api.fetch]);

  // a silent token refresh renews the open hub stream (no reconnect)
  const token = session?.accessToken ?? null;
  const previousToken = useRef(token);
  useEffect(() => {
    if (client && token && previousToken.current !== null && previousToken.current !== token) {
      client.tokenChanged();
    }
    previousToken.current = token;
  }, [client, token]);

  const active = injected !== undefined ? injected : client;
  return (
    <RealtimeProvider client={active} locale={i18n.resolvedLanguage ?? "en"}>
      {children}
      {staffId !== null ? <LiveAlerts staffId={staffId} /> : null}
    </RealtimeProvider>
  );
}
